<template>
    <!-- Decorativo: o status do pedido já está em texto na página -->
    <svg class="osa" :class="`is-${stage}`" :viewBox="viewBox" aria-hidden="true" focusable="false">
        <!-- Pendente: a ampulheta escorre e vira, devagar -->
        <g v-if="stage === 'pending'" class="osa-hourglass">
            <rect class="osa-frame" x="12" y="3" width="24" height="3.2" rx="1.6" />
            <rect class="osa-frame" x="12" y="33.8" width="24" height="3.2" rx="1.6" />
            <path class="osa-glass" d="M15 6.2 h18 c0 8 -6 10.5 -6 13.8 c0 3.3 6 5.8 6 13.8 h-18 c0 -8 6 -10.5 6 -13.8 c0 -3.3 -6 -5.8 -6 -13.8 z" />
            <path class="osa-sand osa-sand-top" d="M17.4 9 h13.2 c-1 4.6 -4.6 7 -6.6 9.8 c-2 -2.8 -5.6 -5.2 -6.6 -9.8 z" />
            <path class="osa-sand osa-sand-bottom" d="M17.6 33.6 c1.2 -5 4.4 -6.6 6.4 -8.8 c2 2.2 5.2 3.8 6.4 8.8 z" />
            <line class="osa-stream" x1="24" y1="19.5" x2="24" y2="31" />
        </g>

        <!-- Integrando: baixando do canal -->
        <g v-else-if="stage === 'integrating'">
            <path class="osa-cloud" d="M14 30 a7 7 0 0 1 -0.6 -14 a9.5 9.5 0 0 1 18.2 -2.6 a7.2 7.2 0 0 1 3.2 16.6 z" />
            <g class="osa-arrow">
                <line x1="24" y1="17" x2="24" y2="27" />
                <polyline points="20,23.5 24,27.5 28,23.5" />
            </g>
        </g>

        <!-- Associação pendente: a caixa do Muven ainda aberta, porque falta saber qual produto vai nela -->
        <g v-else-if="stage === 'pendingAssignment'" class="osa-parcel osa-parcel-open">
            <g class="osa-parcel-body">
                <!-- Aba de trás, aberta para cima -->
                <polygon class="osa-face-top osa-flap-back" points="23,14 52,14 47,1 18,1" />
                <!-- Aba da esquerda, caída para fora -->
                <polygon class="osa-face-top osa-flap-left" points="8,22 23,14 14,8 -1,15" />
                <polygon class="osa-face-side" points="40,22 55,14 55,40 40,48" />
                <!-- Fundo da caixa visto pela abertura -->
                <polygon class="osa-inside" points="8,22 23,14 55,14 40,22" />
                <polygon class="osa-face-front" points="8,22 40,22 40,48 8,48" />
                <g class="osa-stamp">
                    <image class="osa-logo" :href="muvenLogo" x="15" y="27.5" width="18" height="18" />
                </g>
            </g>
            <g class="osa-badge-q">
                <circle cx="56" cy="9" r="7.6" />
                <text x="56" y="12.9" text-anchor="middle">?</text>
            </g>
        </g>

        <!-- Aprovado: a caixa do pedido é fechada, recebe a fita e o carimbo do Muven -->
        <!-- Cancelado: a mesma caixa, sacudida, com o X vermelho -->
        <g v-else-if="stage === 'approved' || stage === 'canceled'" class="osa-parcel">
            <g class="osa-parcel-body">
                <polygon class="osa-face-side" points="40,22 55,14 55,40 40,48" />
                <polygon class="osa-face-front" points="8,22 40,22 40,48 8,48" />
                <!-- Adesivo: o foguete do Muven -->
                <g class="osa-stamp">
                    <image class="osa-logo" :href="muvenLogo" x="15" y="27.5" width="18" height="18" />
                </g>
            </g>
            <g class="osa-lid">
                <polygon class="osa-face-top" points="8,22 23,14 55,14 40,22" />
                <line class="osa-fold" x1="15.5" y1="18" x2="47.5" y2="18" />
            </g>
            <g class="osa-tape">
                <polygon points="21,22 27,22 42,14 36,14" />
                <rect x="21" y="22" width="6" height="3.6" rx=".6" />
            </g>
            <g v-if="stage === 'approved'" class="osa-check">
                <circle cx="54" cy="12" r="6.4" />
                <path d="M51 12.2 l2 2 l4 -4.2" />
            </g>
            <template v-else>
                <!-- Tracinhos de "impacto" em volta da caixa -->
                <g class="osa-burst">
                    <line x1="1" y1="27" x2="-4" y2="25.5" />
                    <line x1="2" y1="33" x2="-3.5" y2="34" />
                    <line x1="3.5" y1="21" x2="0" y2="17" />
                    <line x1="61" y1="4" x2="64.5" y2=".5" />
                    <line x1="63" y1="10" x2="68" y2="9" />
                    <line x1="62.5" y1="16" x2="67" y2="18.5" />
                </g>
                <g class="osa-badge-x">
                    <circle cx="54" cy="10" r="7" />
                    <path d="M51.2 7.2 l5.6 5.6 M56.8 7.2 l-5.6 5.6" />
                </g>
            </template>
        </g>

        <!-- Faturado: a nota fiscal do pedido, emitida pelo Muven -->
        <g v-else-if="stage === 'billed'" class="osa-invoice">
            <g class="osa-sheet-wrap">
                <!-- Papel com a borda de baixo picotada, como um cupom -->
                <path class="osa-sheet" d="M14 6 h30 a2 2 0 0 1 2 2 v36 l-3 2.4 l-3 -2.4 l-3 2.4 l-3 -2.4 l-3 2.4 l-3 -2.4 l-3 2.4 l-3 -2.4 l-3 2.4 l-3 -2.4 v-36 a2 2 0 0 1 2 -2 z" />
                <image class="osa-logo" :href="muvenLogo" x="17" y="9" width="10" height="10" />
                <line class="osa-row osa-row-brand" x1="29.5" y1="12.6" x2="40" y2="12.6" />
                <line class="osa-row osa-row-1" x1="17.5" y1="22" x2="42" y2="22" />
                <line class="osa-row osa-row-2" x1="17.5" y1="26.5" x2="37" y2="26.5" />
                <line class="osa-row osa-row-3" x1="17.5" y1="31" x2="40" y2="31" />
                <text class="osa-amount" x="17.5" y="40.6">R$</text>
                <line class="osa-row osa-row-total" x1="29" y1="38.6" x2="42" y2="38.6" />
            </g>
            <g class="osa-check">
                <circle cx="47" cy="34" r="6.4" />
                <path d="M44 34.2 l2 2 l4 -4.2" />
            </g>
        </g>

        <!-- Finalizado: a bandeira quadriculada, uma tremulada -->
        <g v-else-if="stage === 'finalized'">
            <line class="osa-pole" x1="15" y1="5" x2="15" y2="38" />
            <g class="osa-flag">
                <rect class="osa-flag-bg" x="15" y="6" width="22" height="15" />
                <path class="osa-flag-check" d="M15 6 h5.5 v5 h-5.5 z M26 6 h5.5 v5 h-5.5 z M20.5 11 h5.5 v5 h-5.5 z M31.5 11 h5.5 v5 h-5.5 z M15 16 h5.5 v5 h-5.5 z M26 16 h5.5 v5 h-5.5 z" />
            </g>
        </g>
    </svg>
</template>

<script>
/**
 * Ilustração da etapa atual do pedido, no mesmo lugar do caminhão. Cada uma
 * conta o que está acontecendo; só as de espera ficam em loop, e bem devagar.
 */
export default {
    name: 'OrderStageArtCmp',
    props: {
        // 'pending' | 'integrating' | 'pendingAssignment' | 'approved' | 'billed' | 'finalized' | 'canceled'
        stage: {
            type: String,
            required: true,
        },
    },
    computed: {
        // Recorte de cada desenho: a caixa e a nota são mais detalhadas e ganham mais área
        viewBox() {
            return {approved: '0 0 64 52', canceled: '0 0 64 52', pendingAssignment: '0 0 64 52', billed: '9 2 46 48'}[this.stage] || '0 0 48 40'
        },
        // Ícone oficial do Muven (o foguete), o mesmo do menu lateral
        muvenLogo() {
            return require('@/assets/logo-muven.png')
        },
    },
}
</script>

<style lang="scss" scoped>
$ease-out: cubic-bezier(.22, 1, .36, 1);

.osa {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;

    * {
        transform-box: fill-box;
    }
}

// Pendente
.osa-frame {
    fill: var(--brand-strong);
}

.osa-glass {
    fill: var(--surface-1);
    stroke: var(--border-strong);
    stroke-width: 1.2;
}

.osa-sand {
    fill: var(--warning-solid);
}

.osa-sand-top {
    transform-origin: center bottom;
}

.osa-sand-bottom {
    transform-origin: center bottom;
}

.osa-stream {
    stroke: var(--warning-solid);
    stroke-width: 1;
    stroke-dasharray: 1.5 1.5;
}

.is-pending {
    // Uma volta só: escorre, vira e para (a areia cheia fica em cima de novo: esperando)
    .osa-hourglass {
        transform-origin: center;
        animation: osa-flip 3.2s ease-in-out .5s both;
    }

    .osa-sand-top {
        animation: osa-drain 3.2s linear .5s both;
    }

    .osa-sand-bottom {
        animation: osa-fill 3.2s linear .5s both;
    }

    .osa-stream {
        animation: osa-stream 3.2s linear .5s both;
    }
}

// Escorre até 80% do ciclo e vira no fim
@keyframes osa-flip {
    0%, 80% { transform: rotate(0); }
    100% { transform: rotate(180deg); }
}

@keyframes osa-drain {
    0% { transform: scaleY(1); }
    80%, 100% { transform: scaleY(.05); }
}

@keyframes osa-fill {
    0% { transform: scaleY(.05); }
    80%, 100% { transform: scaleY(1); }
}

@keyframes osa-stream {
    0%, 78% { opacity: 1; stroke-dashoffset: 0; }
    40% { stroke-dashoffset: -6; }
    80%, 100% { opacity: 0; }
}

// Integrando
.osa-cloud {
    fill: var(--surface-3);
    stroke: var(--border-strong);
    stroke-width: 1.2;
}

.osa-arrow {
    fill: none;
    stroke: var(--brand);
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
    // Desce duas vezes e para (loop infinito cansa)
    animation: osa-download 2.2s ease-in-out .4s 2 both;
}

@keyframes osa-download {
    0% { opacity: 0; transform: translateY(-5px); }
    35%, 100% { opacity: 1; transform: translateY(0); }
}

// Associação pendente: caixa aberta e selo de dúvida
.osa-inside {
    fill: color-mix(in srgb, var(--warning-solid) 55%, var(--shadow-ink));
}

.osa-flap-back {
    transform-origin: center bottom;
}

.osa-flap-left {
    transform-origin: right bottom;
}

.osa-badge-q {
    transform-origin: center;

    circle {
        fill: var(--warning-solid);
        stroke: var(--surface-1);
        stroke-width: 1.4;
    }

    text {
        fill: var(--white);
        font-family: 'Bricolage Grotesque', 'Source Sans Pro', system-ui, sans-serif;
        font-size: 11px;
        font-weight: 800;
    }
}

// Sequência única: a caixa assenta, as abas terminam de abrir e o "?" aparece e balança uma vez
.is-pendingAssignment {
    .osa-flap-left {
        animation: osa-open-left .5s cubic-bezier(.34, 1.3, .64, 1) .7s both;
    }

    .osa-flap-back {
        animation: osa-open-back .5s cubic-bezier(.34, 1.3, .64, 1) .8s both;
    }

    .osa-stamp {
        transform-origin: center;
        animation: osa-stamp .4s cubic-bezier(.2, 1.4, .5, 1) 1.05s both;
    }

    .osa-badge-q {
        animation: osa-pop .4s cubic-bezier(.2, 1.5, .5, 1) 1.35s both, osa-wobble .6s ease-in-out 1.8s 1;
    }
}

@keyframes osa-open-left {
    from { transform: scale(.3); }
    to { transform: scale(1); }
}

@keyframes osa-open-back {
    from { transform: scaleY(.2); }
    to { transform: scaleY(1); }
}

@keyframes osa-wobble {
    0%, 100% { transform: rotate(0); }
    25% { transform: rotate(-14deg); }
    55% { transform: rotate(10deg); }
    80% { transform: rotate(-4deg); }
}

// Aprovado: caixa de papelão em dois tons chapados, sem brilho
.osa-face-front {
    fill: color-mix(in srgb, var(--warning-solid) 58%, var(--white));
}

.osa-face-side {
    fill: color-mix(in srgb, var(--warning-solid) 78%, var(--shadow-ink));
}

.osa-face-top {
    fill: color-mix(in srgb, var(--warning-solid) 40%, var(--white));
}

.osa-face-front,
.osa-face-side,
.osa-face-top {
    stroke: color-mix(in srgb, var(--warning-fg) 70%, transparent);
    stroke-width: .8;
    stroke-linejoin: round;
}

.osa-fold {
    stroke: color-mix(in srgb, var(--warning-fg) 45%, transparent);
    stroke-width: .7;
}

.osa-tape {
    fill: color-mix(in srgb, var(--warning-solid) 22%, var(--white));
    stroke: color-mix(in srgb, var(--warning-fg) 35%, transparent);
    stroke-width: .5;
}

.osa-logo {
    // Leve sombra de adesivo colado no papelão/papel, sem brilho
    filter: drop-shadow(0 .6px .4px color-mix(in srgb, var(--shadow-ink) 35%, transparent));
}

.osa-check {
    circle {
        fill: var(--brand-fill);
        stroke: var(--surface-1);
        stroke-width: 1.4;
    }

    path {
        fill: none;
        stroke: var(--white);
        stroke-width: 1.8;
        stroke-linecap: round;
        stroke-linejoin: round;
    }
}

// Sequência única: a caixa assenta, a tampa fecha, a fita passa, o M é carimbado e o check aparece
.osa-parcel-body {
    animation: osa-settle .5s cubic-bezier(.34, 1.4, .64, 1) .3s both;
}

.osa-lid {
    animation: osa-lid .4s cubic-bezier(.34, 1.3, .64, 1) .65s both;
}

.osa-tape {
    transform-origin: left bottom;
    animation: osa-tape .35s ease-out 1s both;
}

.osa-stamp {
    transform-origin: center;
    animation: osa-stamp .4s cubic-bezier(.2, 1.4, .5, 1) 1.3s both;
}

.osa-check {
    transform-origin: center;
    animation: osa-pop .4s cubic-bezier(.2, 1.5, .5, 1) 1.65s both;
}

@keyframes osa-settle {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes osa-lid {
    from { opacity: 0; transform: translateY(-7px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes osa-tape {
    from { opacity: 0; transform: scale(.3); }
    to { opacity: 1; transform: scale(1); }
}

// Carimbo: chega grande, bate e assenta
@keyframes osa-stamp {
    0% { opacity: 0; transform: scale(1.5); }
    70% { opacity: 1; transform: scale(.92); }
    100% { opacity: 1; transform: scale(1); }
}

// Cancelado: chega, leva o "tranco" (inclina e balança) e recebe o X
.is-canceled {
    .osa-parcel {
        transform-origin: center bottom;
        animation: osa-jolt .6s ease-out 1.5s both;
    }

    .osa-badge-x {
        transform-origin: center;
        animation: osa-pop .4s cubic-bezier(.2, 1.5, .5, 1) 1.7s both;
    }

    .osa-burst {
        animation: osa-burst .45s ease-out 1.75s both;
    }
}

.osa-badge-x {
    circle {
        fill: var(--danger-solid);
        stroke: var(--surface-1);
        stroke-width: 1.4;
    }

    path {
        stroke: var(--white);
        stroke-width: 2;
        stroke-linecap: round;
    }
}

.osa-burst {
    transform-origin: center;
    stroke: var(--danger-solid);
    stroke-width: 1.6;
    stroke-linecap: round;
}

@keyframes osa-jolt {
    0% { transform: rotate(0); }
    35% { transform: rotate(-13deg); }
    65% { transform: rotate(-5deg); }
    100% { transform: rotate(-9deg); }
}

@keyframes osa-burst {
    from { opacity: 0; transform: scale(.6); }
    to { opacity: 1; transform: scale(1); }
}

@keyframes osa-pop {
    from { opacity: 0; transform: scale(0); }
    to { opacity: 1; transform: scale(1); }
}

// Faturado: a nota (papel claro nos dois temas, como um papel de verdade)
.osa-sheet {
    fill: var(--white);
    stroke: var(--border-strong);
    stroke-width: .9;
    stroke-linejoin: round;
}


.osa-row {
    stroke: color-mix(in srgb, var(--shadow-ink) 22%, var(--white));
    stroke-width: 1.6;
    stroke-linecap: round;
    transform-origin: left center;
}

.osa-row-total {
    stroke: color-mix(in srgb, var(--shadow-ink) 40%, var(--white));
    stroke-width: 2;
}

.osa-amount {
    fill: var(--shadow-ink);
    font-family: 'Bricolage Grotesque', 'Source Sans Pro', system-ui, sans-serif;
    font-size: 7.5px;
    font-weight: 800;
}

// Sequência única: a nota sobe e assenta inclinada, as linhas são escritas, o valor e o check aparecem
.is-billed {
    .osa-sheet-wrap {
        transform-origin: center bottom;
        animation: osa-sheet-in .6s cubic-bezier(.34, 1.3, .64, 1) .3s both;
    }

    .osa-row-1 { animation: osa-write .3s ease-out .85s both; }
    .osa-row-2 { animation: osa-write .3s ease-out 1s both; }
    .osa-row-3 { animation: osa-write .3s ease-out 1.15s both; }
    .osa-row-total { animation: osa-write .3s ease-out 1.35s both; }

    .osa-amount {
        animation: osa-fade .3s ease-out 1.35s both;
    }

    .osa-check {
        transform-origin: center;
        animation: osa-pop .4s cubic-bezier(.2, 1.5, .5, 1) 1.65s both;
    }
}

@keyframes osa-sheet-in {
    from { opacity: 0; transform: translateY(8px) rotate(0); }
    to { opacity: 1; transform: translateY(0) rotate(-6deg); }
}

@keyframes osa-write {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
}

@keyframes osa-fade {
    from { opacity: 0; }
}

// Finalizado: bandeira
.osa-pole {
    stroke: var(--text-muted);
    stroke-width: 1.8;
    stroke-linecap: round;
}

.osa-flag-bg {
    fill: var(--surface-1);
    stroke: var(--text-strong);
    stroke-width: .8;
}

.osa-flag-check {
    fill: var(--text-strong);
}

.osa-flag {
    transform-origin: left center;
    animation: osa-wave 1.6s ease-in-out .9s both;
}

@keyframes osa-wave {
    0%, 100% { transform: skewY(0) scaleX(1); }
    25% { transform: skewY(-6deg) scaleX(.94); }
    50% { transform: skewY(4deg) scaleX(.98); }
    75% { transform: skewY(-2deg) scaleX(.97); }
}

@media (prefers-reduced-motion: reduce) {
    .osa * {
        animation: none !important;
    }
}
</style>
